import { canDownloadEmailVCard, downloadEmailVCard } from './emailVCard';
import { getOS } from './osDetection';

declare const window: any;

export function isNativeContactPlatform(): boolean {
  return typeof window !== 'undefined' && !!window.cordova
    && ['ios', 'android'].includes(window.cordova.platformId);
}

function nativeRequest(action: string, args: string[] = []): Promise<any> {
  return new Promise((resolve, reject) => {
    const cordova = typeof window !== 'undefined' && window.cordova;
    if (!isNativeContactPlatform() || typeof cordova.exec !== 'function') {
      reject(new Error('Contact editor unavailable'));
      return;
    }
    cordova.exec(resolve, reject, 'VVContactEditor', action, args);
  });
}

export function isContactEditorAvailable(): Promise<boolean> {
  if (!isNativeContactPlatform()) { return Promise.resolve(canDownloadEmailVCard()); }
  // Older app binaries do not contain the bridge; keep their button hidden.
  return nativeRequest('available').then(value => value === true).catch(() => false);
}

function browserContactPicker(): any {
  if (typeof navigator === 'undefined' || typeof window === 'undefined'
    || !window.isSecureContext || !['IOS', 'ANDROID'].includes(getOS())) { return null; }
  const contacts = (navigator as any).contacts;
  return contacts && typeof contacts.select === 'function' ? contacts : null;
}

export async function isContactPickerAvailable(): Promise<boolean> {
  if (isNativeContactPlatform()) {
    // Probe separately: older binaries may support creating contacts only.
    return nativeRequest('pickerAvailable').then(value => value === true).catch(() => false);
  }
  const contacts = browserContactPicker();
  if (!contacts) { return false; }
  try {
    return typeof contacts.getProperties !== 'function' || (await contacts.getProperties()).includes('tel');
  } catch (error) {
    return false;
  }
}

// Only the number(s) explicitly selected by the user leave the system picker.
// null means cancelled; an empty array means the selected contact has no number.
export async function pickContactPhoneNumbers(): Promise<string[] | null> {
  try {
    if (isNativeContactPlatform()) {
      const phone = await nativeRequest('pickPhone');
      return typeof phone === 'string' && phone.trim() ? [phone.trim()] : null;
    }
    const contacts = browserContactPicker();
    if (!contacts) { throw new Error('unavailable'); }
    // Invoke select directly inside the tap gesture; do not await a capability probe.
    const selected = await contacts.select(['tel'], { multiple: false });
    if (!selected || !selected.length) { return null; }
    const phones = Array.isArray(selected[0].tel) ? selected[0].tel : [];
    return phones.filter((phone: any) => typeof phone === 'string' && phone.trim())
      .map((phone: string) => phone.trim())
      .filter((phone: string, index: number, values: string[]) => values.indexOf(phone) === index);
  } catch (error) {
    if (error && error.name === 'AbortError') { return null; }
    throw new Error('Could not open the contact picker');
  }
}

export async function openEmailContact(name: string, email: string): Promise<void> {
  if (!name.trim() || !email.trim()) {
    throw new Error('Contact name and email are required');
  }
  try {
    if (isNativeContactPlatform()) {
      // The system editor owns saving/cancelling. Never log the private forwarding address.
      await nativeRequest('create', [name, email]);
    } else {
      // Keep the download within the click gesture. The contact stays entirely local.
      downloadEmailVCard(name, email);
    }
  } catch (error) {
    throw new Error('Could not open the contact editor');
  }
}



// WEBPACK FOOTER //
// ./src/helpers/contactEditor.ts